import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Placeholder from '@tiptap/extension-placeholder';
import {
  FaTrash, FaPrint, FaSpinner, FaBold, FaItalic, FaStrikethrough,
  FaListUl, FaListOl, FaQuoteRight, FaTable, FaUndo, FaRedo, FaHeading, FaParagraph,
  FaImage
} from 'react-icons/fa';
import { useTranslation } from '../../i18n';
import useActivityTracker from '../../hooks/useActivityTracker';
import { useOrganization } from '../../contexts/OrganizationContext';
import logoDefault from '../../assets/img/logo-data-new.png';
import './ReportPage.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// ─── Debounce helper para auto-save ─────────────────────────────────────
const useDebouncedCallback = (cb, delay) => {
  const timerRef = useRef(null);
  return useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => cb(...args), delay);
  }, [cb, delay]);
};

// ─── Documento Tiptap inicial vacio ─────────────────────────────────────
const emptyDoc = () => ({ type: 'doc', content: [{ type: 'paragraph' }] });

// Normaliza el contenido leido de la BD: si es array (legacy) o vacio, devuelve doc vacio.
// Si ya es doc Tiptap, lo devuelve tal cual.
const normalizeContent = (raw) => {
  if (!raw) return emptyDoc();
  if (Array.isArray(raw)) return emptyDoc();  // legacy → fresh empty doc
  if (typeof raw === 'object' && raw.type === 'doc') return raw;
  return emptyDoc();
};

const ReportPage = () => {
  const { id_report } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthenticator(c => [c.user]);
  const username = user?.username;
  const { t } = useTranslation();
  const { trackAction } = useActivityTracker(username, 'custom_reports');
  const { logoUrl, displayName } = useOrganization();

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // ─── Persistir cambios (debounced) ──────────────────────────────────
  const persist = useCallback(async (patch) => {
    if (!username) return;
    try {
      setIsSaving(true);
      const res = await fetch(`${STRANDS_API_URL}/api/reports/${id_report}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, ...patch })
      });
      const data = await res.json();
      if (data?.success) {
        setSavedAt(new Date());
      }
    } catch (err) {
      console.error('[ReportPage] save error:', err);
    } finally {
      setIsSaving(false);
    }
  }, [id_report, username]);

  const debouncedSaveDoc = useDebouncedCallback(
    (json) => persist({ blocks: json }),
    700
  );
  const debouncedSaveTitle = useDebouncedCallback(
    (title) => persist({ title }),
    700
  );

  // ─── Editor Tiptap ──────────────────────────────────────────────────
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'report-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: t('report.placeholder') || 'Empieza a escribir, o usa los botones de arriba para insertar elementos…'
      })
    ],
    content: emptyDoc(),
    onUpdate: ({ editor }) => {
      debouncedSaveDoc(editor.getJSON());
    },
  });

  // ─── Carga inicial del reporte ──────────────────────────────────────
  useEffect(() => {
    if (!id_report || !username || !editor) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const res = await fetch(`${STRANDS_API_URL}/api/reports/${id_report}?username=${encodeURIComponent(username)}`);
        if (!res.ok) throw new Error('No se pudo cargar el reporte');
        const data = await res.json();
        if (cancelled || !data.success) return;
        setReport(data.report);
        const doc = normalizeContent(data.report.blocks);
        // setContent con falso para evitar disparar onUpdate (no auto-guardar al cargar)
        editor.commands.setContent(doc, false);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id_report, username, editor]);

  // ─── Acciones del reporte ────────────────────────────────────────────
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setReport(prev => ({ ...prev, title: newTitle }));
    debouncedSaveTitle(newTitle);
  };

  const handleDeleteReport = async () => {
    if (!window.confirm(t('report.deleteConfirm'))) return;
    try {
      await fetch(`${STRANDS_API_URL}/api/reports/${id_report}?username=${encodeURIComponent(username)}`, { method: 'DELETE' });
      trackAction('delete_report', { report_id: id_report });
      // Sidebar se entera para sacarlo de la lista sin recargar la página.
      document.dispatchEvent(new CustomEvent('reportListChanged', {
        detail: { action: 'deleted', id: id_report }
      }));
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrint = () => {
    trackAction('print_report', { report_id: id_report });

    // Estrategia: iframe oculto con HTML completo del reporte +
    // membrete inline. Al imprimir, el navegador captura SOLO el
    // contenido del iframe (no la página padre con su sidebar).
    // Es la estrategia más confiable: no abre ventanas (sin popup
    // blockers), no requiere ruta nueva, no depende de cache de
    // router. Funciona en todos los navegadores modernos.

    const editorEl = document.querySelector('.report-editor .ProseMirror');
    const contentHtml = editorEl ? editorEl.innerHTML : '';

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const titleText = report?.title || 'Reporte';
    const orgName = displayName || 'Data Bunker';
    // Logo: si la URL es relativa al frontend, la resolvemos absoluta para
    // que el iframe pueda cargarla (vive en un documento srcdoc, sin base
    // URL del padre).
    const rawLogo = logoUrl || logoDefault;
    const orgLogo = rawLogo && rawLogo.startsWith('/')
      ? `${window.location.origin}${rawLogo}`
      : rawLogo;
    const generatedDate = new Date().toLocaleDateString('es-MX', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
    const userLabel = username || '—';

    const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${esc(titleText)}</title>
<style>
  @page { size: A4; margin: 1.5cm 1.4cm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    color: #1e293b;
    margin: 0;
    padding: 0;
    background: white;
    line-height: 1.55;
    font-size: 14px;
  }
  .doc {
    padding: 8px 0 24px;
  }
  .doc-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 14px;
    border-bottom: 3px solid #073C5C;
    margin-bottom: 22px;
  }
  .doc-header-left { display: flex; align-items: center; gap: 14px; }
  .doc-header img.logo { height: 44px; width: auto; object-fit: contain; }
  .doc-header .org-name { font-weight: 700; font-size: 17px; color: #073C5C; }
  .doc-header .org-sub {
    font-size: 11px; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.05em;
  }
  .doc-header-right { text-align: right; }
  .doc-header .date-label {
    font-size: 10px; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2px;
  }
  .doc-header .date { font-size: 12px; color: #073C5C; font-weight: 600; }
  h1.doc-title {
    font-size: 24px; font-weight: 700; color: #073C5C;
    margin: 0 0 6px; letter-spacing: -0.02em;
  }
  .doc-meta {
    font-size: 12px; color: #64748b;
    margin-bottom: 22px; padding-bottom: 10px;
    border-bottom: 1px solid #e2e8f0;
  }
  .doc-meta strong { color: #1e293b; }
  .doc-body h1 { font-size: 20px; margin: 18px 0 8px; color: #073C5C; }
  .doc-body h2 { font-size: 17px; margin: 16px 0 7px; color: #073C5C; }
  .doc-body h3 { font-size: 14.5px; margin: 12px 0 5px; color: #1e293b; }
  .doc-body p { margin: 7px 0; }
  .doc-body ul, .doc-body ol { margin: 7px 0; padding-left: 22px; }
  .doc-body li { margin: 3px 0; }
  .doc-body blockquote {
    margin: 10px 0; padding: 8px 14px;
    border-left: 3px solid #F68D2E; background: #fff7ed;
    color: #475569; border-radius: 0 4px 4px 0;
  }
  .doc-body table {
    border-collapse: collapse; width: 100%; margin: 12px 0;
    font-size: 12px;
    /* Sin page-break-inside: avoid en tablas — antes generaba huecos
       enormes cuando una tabla larga no entraba en lo que quedaba de la
       hoja. Permitimos que se corte y usamos thead repetido. */
  }
  /* thead como table-header-group hace que el encabezado se repita en
     cada página cuando la tabla se parte. Y atomic rows: cada fila NO se
     parte a la mitad. */
  .doc-body thead { display: table-header-group; }
  .doc-body tr { page-break-inside: avoid; }
  .doc-body th, .doc-body td {
    border: 1px solid #cbd5e1; padding: 6px 9px;
    text-align: left; vertical-align: top;
  }
  .doc-body th { background: #f1f5f9; font-weight: 600; color: #073C5C; }
  .doc-body img {
    max-width: 100%; height: auto; margin: 12px 0;
    /* Imágenes (gráficas) SÍ deben mantenerse enteras — son visualmente
       inseparables. Genera un hueco menor pero vale la pena. */
    page-break-inside: avoid;
    border: 1px solid #e2e8f0; border-radius: 4px;
  }
  .doc-footer {
    margin-top: 28px; padding-top: 10px;
    border-top: 1px solid #e2e8f0;
    display: flex; justify-content: space-between;
    font-size: 10px; color: #94a3b8; letter-spacing: 0.03em;
  }
  /* page-break-after: avoid en headings se eliminó: antes empujaba el
     título a la siguiente página si no había suficiente espacio debajo,
     y dejaba un hueco grande arriba. Aceptamos que un título pueda
     quedar al final de página; el flujo natural se siente más continuo. */
</style>
</head>
<body>
  <div class="doc">
    <header class="doc-header">
      <div class="doc-header-left">
        <img class="logo" src="${esc(orgLogo)}" alt="${esc(orgName)}" onerror="this.style.display='none'">
        <div>
          <div class="org-name">${esc(orgName)}</div>
          <div class="org-sub">Reporte de análisis</div>
        </div>
      </div>
      <div class="doc-header-right">
        <div class="date-label">Generado</div>
        <div class="date">${esc(generatedDate)}</div>
      </div>
    </header>
    <h1 class="doc-title">${esc(titleText)}</h1>
    <div class="doc-meta">Preparado por <strong>${esc(userLabel)}</strong></div>
    <div class="doc-body">${contentHtml}</div>
    <footer class="doc-footer">
      <span>${esc(orgName)} · Documento confidencial</span>
      <span>Generado automáticamente</span>
    </footer>
  </div>
</body>
</html>`;

    // iframe oculto. position:fixed + 0×0 + opacity 0 → invisible al
    // usuario pero el navegador igual lo renderiza.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);

    const cleanup = () => {
      // Pequeño delay porque algunos navegadores siguen necesitando el
      // iframe vivo mientras el diálogo de imprimir está abierto.
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch (e) {}
      }, 1500);
    };

    const triggerPrint = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) {
          console.warn('[ReportPage] iframe sin contentWindow');
          cleanup();
          return;
        }
        win.focus();
        win.print();
      } catch (err) {
        console.error('[ReportPage] Error al imprimir iframe:', err);
      } finally {
        cleanup();
      }
    };

    iframe.onload = () => {
      const doc = iframe.contentDocument;
      if (!doc) {
        console.warn('[ReportPage] iframe sin contentDocument');
        cleanup();
        return;
      }
      const imgs = Array.from(doc.images || []);
      const pending = imgs.filter(im => !im.complete);
      if (pending.length === 0) {
        // Aún así esperamos 1 tick para que Chrome termine layout.
        setTimeout(triggerPrint, 100);
        return;
      }
      let left = pending.length;
      const dec = () => { if (--left <= 0) setTimeout(triggerPrint, 100); };
      pending.forEach(im => {
        im.addEventListener('load', dec, { once: true });
        im.addEventListener('error', dec, { once: true });
      });
      // Fallback de seguridad: si alguna imagen no responde en 4s,
      // disparamos print de todas formas.
      setTimeout(() => { if (left > 0) triggerPrint(); }, 4000);
    };

    // srcdoc dispara onload con el documento ya parseado y renderizado.
    iframe.srcdoc = fullHtml;
  };

  // ─── Render ──────────────────────────────────────────────────────────
  if (isLoading) {
    return <div className="report-page-loading"><FaSpinner className="spinner" /> {t('report.loading')}</div>;
  }
  if (error) {
    return <div className="report-page-error">{error}</div>;
  }
  if (!report) return null;

  return (
    <div className="report-page">
      {/* Toolbar fijo arriba */}
      <div className="report-toolbar no-print">
        <div className="report-toolbar-left">
          <input
            className="report-title-input"
            value={report.title}
            onChange={handleTitleChange}
            placeholder={t('report.titlePlaceholder')}
          />
          <SaveIndicator isSaving={isSaving} savedAt={savedAt} t={t} />
        </div>
        <div className="report-toolbar-right">
          <button className="report-btn" onClick={handlePrint} title={t('report.exportPdfTitle')}>
            <FaPrint /> {t('report.exportPdf')}
          </button>
          <button className="report-btn danger" onClick={handleDeleteReport}>
            <FaTrash /> {t('report.deleteReport')}
          </button>
        </div>
      </div>

      {/* Barra de formato del editor */}
      {editor && <EditorToolbar editor={editor} t={t} />}

      {/* Título visible al imprimir */}
      <h1 className="report-title-print print-only">{report.title}</h1>

      {/* Editor WYSIWYG */}
      <EditorContent editor={editor} className="report-editor" />
    </div>
  );
};

// ─── Indicador "Guardando…" / "Guardado hace Xs" ────────────────────────
const SaveIndicator = ({ isSaving, savedAt, t }) => {
  if (isSaving) {
    return <span className="save-indicator saving"><FaSpinner className="spinner-small" /> {t('report.saving')}</span>;
  }
  if (savedAt) {
    return <span className="save-indicator saved">✓ {t('report.savedJustNow') || 'Guardado'}</span>;
  }
  return null;
};

// ─── Toolbar de formato (bold, headings, listas, tablas, etc.) ──────────
const EditorToolbar = ({ editor, t }) => {
  const isInTable = editor.isActive('table');
  // Tiptap marca 'image' como activo cuando el cursor está sobre el nodo de
  // imagen o cuando el usuario lo seleccionó con click. Aprovechamos eso
  // para mostrar el botón "Eliminar gráfica" solo cuando aplica.
  const isOnImage = editor.isActive('image');

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const deleteImage = () => {
    // deleteSelection() funciona si el nodo de imagen está seleccionado;
    // si no, el fallback es buscar el nodo y borrarlo.
    if (!editor.chain().focus().deleteSelection().run()) {
      editor.chain().focus().selectParentNode().deleteSelection().run();
    }
  };

  const setHeading = (level) => {
    if (level === 0) editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level }).run();
  };

  const headingValue = editor.isActive('heading', { level: 1 }) ? '1'
    : editor.isActive('heading', { level: 2 }) ? '2'
    : editor.isActive('heading', { level: 3 }) ? '3'
    : '0';

  return (
    <div className="editor-toolbar no-print">
      <select
        className="toolbar-select"
        value={headingValue}
        onChange={(e) => setHeading(parseInt(e.target.value))}
        title={t('report.toolbar.heading') || 'Estilo de texto'}
      >
        <option value="0">{t('report.toolbar.paragraph') || 'Párrafo'}</option>
        <option value="1">{t('report.toolbar.h1') || 'Título 1'}</option>
        <option value="2">{t('report.toolbar.h2') || 'Título 2'}</option>
        <option value="3">{t('report.toolbar.h3') || 'Título 3'}</option>
      </select>

      <div className="toolbar-divider" />

      <ToolbarBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrita (Ctrl+B)">
        <FaBold />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Cursiva (Ctrl+I)">
        <FaItalic />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Tachado">
        <FaStrikethrough />
      </ToolbarBtn>

      <div className="toolbar-divider" />

      <ToolbarBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista con viñetas">
        <FaListUl />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada">
        <FaListOl />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Cita">
        <FaQuoteRight />
      </ToolbarBtn>

      <div className="toolbar-divider" />

      <ToolbarBtn onClick={insertTable} title={t('report.toolbar.insertTable') || 'Insertar tabla'}>
        <FaTable />
      </ToolbarBtn>
      {isInTable && (
        <ToolbarBtn
          onClick={() => editor.chain().focus().deleteTable().run()}
          title={t('report.toolbar.deleteTable') || 'Eliminar tabla'}
          className="toolbar-btn-danger"
        >
          <FaTable style={{ marginRight: 2 }} /> 🗑
        </ToolbarBtn>
      )}
      {isOnImage && (
        <ToolbarBtn
          onClick={deleteImage}
          title={t('report.toolbar.deleteImage') || 'Eliminar gráfica/imagen'}
          className="toolbar-btn-danger"
        >
          <FaImage style={{ marginRight: 2 }} /> 🗑
        </ToolbarBtn>
      )}

      <div className="toolbar-spacer" />

      <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer (Ctrl+Z)">
        <FaUndo />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer (Ctrl+Y)">
        <FaRedo />
      </ToolbarBtn>
    </div>
  );
};

const ToolbarBtn = ({ active, onClick, disabled, title, children, className = '' }) => (
  <button
    className={`toolbar-btn ${active ? 'active' : ''} ${className}`}
    onClick={onClick}
    disabled={disabled}
    title={title}
    type="button"
  >
    {children}
  </button>
);

export default ReportPage;
